/**
 * Reservation TTL outbox event schemas
 *
 * Defines the payload shape for:
 * - `reservation.ttl_expired`  — emitted when a reservation is deleted due to TTL expiry
 * - `reservation.ttl_expiring` — emitted as a warning before TTL expiry
 *
 * Both schemas share the same payload shape. Only SOFT_RESERVATION and
 * HARD_RESERVATION statuses are valid — OPTION and SOLD are excluded because
 * TTL auto-expiry only applies to timed reservation windows.
 *
 * Traceability: REQ-4.4 -> reservation-ttl-auto-expiry
 */
import { z } from 'zod';

/**
 * Append-only event type strings for reservation TTL events.
 * Downstream consumers rely on these exact values — renaming is forbidden.
 */
export const RESERVATION_TTL_EXPIRING_EVENT_TYPE = 'reservation.ttl_expiring' as const;
export const RESERVATION_TTL_EXPIRED_EVENT_TYPE = 'reservation.ttl_expired' as const;

const ReservationTtlEventPayloadSchema = z.object({
  id: z.number().int().positive(),
  mediaUnitId: z.number().int().positive(),
  offerId: z.number().int().positive().nullable(),
  holderUserId: z.number().int().positive(),
  status: z.enum(['SOFT_RESERVATION', 'HARD_RESERVATION']),
  dateFrom: z.string(), // ISO date, e.g. '2026-06-01'
  dateTo: z.string(), // ISO date
  ttlExpiresAt: z.string(), // ISO datetime, e.g. '2026-05-10T12:00:00.000Z'
});

export const ReservationTtlExpiredEventSchema = ReservationTtlEventPayloadSchema;
export const ReservationTtlExpiringEventSchema = ReservationTtlEventPayloadSchema;

export type ReservationTtlExpiredEvent = z.infer<typeof ReservationTtlExpiredEventSchema>;
export type ReservationTtlExpiringEvent = z.infer<typeof ReservationTtlExpiringEventSchema>;
